import { useState } from "react";
import { motion } from "framer-motion";
import { Eye, EyeOff, Loader2, TriangleAlert } from "lucide-react";
import { cn } from "../lib/utils";

/**
 * v2 auth form controls. Unlike the demo originals these are controlled
 * (value/onChange/onBlur/error) so they slot into the existing Formik logic.
 */
export function AuthField({
  label,
  type = "text",
  placeholder,
  icon: Icon,
  trailing,
  autoComplete,
  name,
  value,
  onChange,
  onBlur,
  error,
}) {
  const [visible, setVisible] = useState(false);
  const isPassword = type === "password";
  const inputType = isPassword && visible ? "text" : type;

  return (
    <label className="block">
      <span className="mb-1.5 flex items-center justify-between text-[12.5px] font-medium text-foreground/80">
        {label}
        {trailing}
      </span>
      <div className="relative">
        {Icon && (
          <Icon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-2" />
        )}
        <input
          type={inputType}
          name={name}
          placeholder={placeholder}
          autoComplete={autoComplete}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          // Inline like the template's boost field: the unlayered
          // `* { border-color }` rule in v2-tokens.css beats border-*
          // utilities, so the class alone never shows the red border.
          style={error ? { borderColor: "var(--danger)" } : undefined}
          className={cn(
            "h-11 w-full rounded-xl border bg-[var(--surface)] text-[14px] text-foreground placeholder:text-muted-2 outline-none transition-colors focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent-soft)]",
            error ? "border-[var(--danger)]" : "border-[var(--border)]",
            Icon ? "pl-9" : "pl-3.5",
            isPassword ? "pr-10" : "pr-3",
          )}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setVisible((v) => !v)}
            aria-label={visible ? "Hide password" : "Show password"}
            className="absolute right-2.5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-muted-2 transition-colors hover:text-foreground"
          >
            {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        )}
      </div>
      {error && <span className="mt-1.5 block text-[12px] text-[var(--danger)]">{error}</span>}
    </label>
  );
}

export function AuthSubmit({ children, loading, disabled, onClick }) {
  return (
    <motion.button
      type="submit"
      onClick={onClick}
      disabled={disabled || loading}
      whileTap={{ scale: 0.98 }}
      whileHover={{ y: -1 }}
      transition={{ type: "spring", stiffness: 400, damping: 22 }}
      className="flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-[var(--accent)] text-[14px] font-semibold text-white shadow-[var(--shadow-glow)] transition-colors hover:bg-[var(--accent-bright)] disabled:cursor-not-allowed disabled:opacity-60"
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
      {children}
    </motion.button>
  );
}

/**
 * Inline error alert shown above the form (e.g. login/signup failures).
 * Plain mode: <AuthAlert>message</AuthAlert>.
 * Rich mode: title + description + optional action node (e.g. a "Sign in
 * instead" link when the email is already registered).
 */
export function AuthAlert({ children, title, description, action }) {
  if (!children && !title) return null;

  if (title) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
        role="alert"
        className="mb-4 flex items-start gap-3 rounded-xl border border-[color-mix(in_oklab,var(--danger)_30%,transparent)] bg-[var(--danger-soft)] p-3.5"
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[color-mix(in_oklab,var(--danger)_18%,transparent)]">
          <TriangleAlert className="h-4.5 w-4.5 text-[var(--danger)]" />
        </span>
        <div className="min-w-0 pt-0.5">
          <p className="text-[13.5px] font-semibold leading-snug text-[var(--danger)]">
            {title}
          </p>
          {description && (
            <p className="mt-0.5 text-[12.5px] leading-relaxed text-foreground/70">
              {description}
            </p>
          )}
          {action && <div className="mt-2">{action}</div>}
        </div>
      </motion.div>
    );
  }

  return (
    <div
      role="alert"
      className="mb-4 rounded-xl border border-[color-mix(in_oklab,var(--danger)_35%,transparent)] bg-[var(--danger-soft)] px-3.5 py-2.5 text-[13px] font-medium text-[var(--danger)]"
    >
      {children}
    </div>
  );
}
